import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {forkJoin} from 'rxjs';
import {GenderViewModel} from '../../../viewModels/gender.viewModel';
import {UsersService} from '../../../services/users.service';
import {UserProfileViewModel} from '../../../viewModels/userProfile.viewModel';
import {NotificationsService} from '../../../services/notification.service';
import {LocalisationService} from '../../../services/localisation.service';
import {BodyTypeViewModel} from '../../../viewModels/bodyType.viewModel';
import {Languages} from '../../../enums/languages';
import {BsModalRef, BsModalService} from 'ngx-bootstrap';
import {BsModalEventsEmitter} from '../../../events/bsModal.events';
import {DialCodeSelectorModalComponent} from '../dial-code-selector-modal/dial-code-selector-modal.component';

declare const require: (path: string) => { [key: string]: string; };

@Component({
    selector: 'app-personal-info-section',
    templateUrl: './personal-info-section.component.html',
    styleUrls: ['./personal-info-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class PersonalInfoSectionComponent implements OnInit {
    @Input() editable = true;
    @Input() notifications = true;
    @Input() useSwiper = true;

    loading = true;
    firstName: FormControl = new FormControl('', [Validators.required]);
    surname: FormControl = new FormControl('', [Validators.required]);
    phone: FormControl = new FormControl('', [Validators.required]);
    age: FormControl = new FormControl('', [Validators.required, Validators.min(1), Validators.max(Infinity)]);
    genders: GenderViewModel[];
    currentlySelectedGender: GenderViewModel;
    mailCampaign = true;

    weight: FormControl = new FormControl('', [Validators.required]);
    target_weight: FormControl = new FormControl('', [Validators.required]);
    height: FormControl = new FormControl('', [Validators.required, Validators.min(1), Validators.max(Infinity)]);
    bodyTypes: BodyTypeViewModel[];
    currentlySelectedBodyType: BodyTypeViewModel;

    dialCodes: { [key: string]: string; };
    selectedPhoneDialCode = '';

    dialCodeModal: BsModalRef;

    constructor(
        private readonly usersService: UsersService,
        public readonly changeDetectorRef: ChangeDetectorRef,
        private readonly notificationService: NotificationsService,
        private readonly localisationService: LocalisationService,
        private readonly bsModalService: BsModalService,
        private readonly bsModalEventsEmitter: BsModalEventsEmitter,
    ) {
        this.dialCodes = require(`../../../../../../../config/country_dial_codes.json`);

        const language = this.localisationService.getLanguage();

        // if (language && language === Languages.Bulgarian) {
        this.selectedPhoneDialCode = '+359';
        // }
    }

    ngOnInit() {
        forkJoin(
            this.usersService.getUserGenders(),
            this.usersService.getUserBodyTypes()
        )
            .subscribe(([genders, bodyTypes]) => {
                this.genders = genders;
                this.bodyTypes = bodyTypes;
                this.loading = false;

                this.changeDetectorRef.detectChanges();

                if (this.usersService.userProfile) {
                    this.initCurrentUserProfile(this.usersService.userProfile);
                }
            });
    }

    onGenderSelected(gender: string) {
        this.currentlySelectedGender = this.genders.find(g => g.gender === gender);
    }

    onSelectedBodyType(id: BodyTypeViewModel) {
        this.currentlySelectedBodyType = id;
    }

    updateProfile() {
        this.loading = true;
        this.usersService.updateUserProfile({
            first_name: this.firstName.value,
            last_name: this.surname.value,
            dial_code: this.selectedPhoneDialCode,
            phone: this.phone.value,
            age: this.age.value,
            mail_campaign: this.mailCampaign,
            weight: this.weight.value,
            target_weight: this.target_weight.value,
            body_type: {
                id: this.currentlySelectedBodyType.id
            },
            height: this.height.value,
            gender: {
                id: this.currentlySelectedGender.id
            }
        })
            .subscribe(() => {
                this.loading = false;
                this.changeDetectorRef.detectChanges();

                if (this.notifications) {
                    this.notificationService.success(this.localisationService.localise('settings_profile_updated_successfully'));
                }
            });
    }

    getBodyTypeTranslationKey(bodyTypeId: number): string {
        if (bodyTypeId === 4) {
            return 'ectomorph_text';
        }

        if (bodyTypeId === 5) {
            return 'mesomorph_text';
        }

        return 'endomorph_text';
    }

    openDialCodeModal() {
        this.dialCodeModal = this.bsModalService.show(DialCodeSelectorModalComponent, {
            backdrop: true
        });

        this.dialCodeModal.content.personalOrWizardComponent = this;
        this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.dialCodeModal);
    }

    get saveButtonIsDisabled(): boolean {
        return !this.weight.valid || !this.target_weight.valid || !this.phone.valid || !this.height.valid || !this.currentlySelectedBodyType || !this.firstName.valid || !this.surname.valid || !this.age.valid || !this.currentlySelectedGender;
    }

    private initCurrentUserProfile(userProfile: UserProfileViewModel) {
        this.firstName.setValue(userProfile.first_name);
        this.surname.setValue(userProfile.last_name);
        this.phone.setValue((userProfile.phone === null ? '' : userProfile.phone));
        this.age.setValue(userProfile.age);
        this.mailCampaign = userProfile.mail_campaign;
        this.weight.setValue(userProfile.weight);
        this.target_weight.setValue(userProfile.target_weight);
        this.height.setValue(Math.floor(userProfile.height));

        if (userProfile.dial_code !== null && userProfile.dial_code.length > 0) {
            this.selectedPhoneDialCode = userProfile.dial_code;
        }

        this.currentlySelectedBodyType = userProfile.body_type ? this.bodyTypes.find(bt => bt.id === userProfile.body_type.id) : undefined;
        this.currentlySelectedGender = userProfile.gender ? this.genders.find(g => g.id === userProfile.gender.id) : undefined;

        this.changeDetectorRef.markForCheck();
    }

    onMailCampaignChange(mailCampaign: boolean) {
        this.mailCampaign = mailCampaign;
        this.changeDetectorRef.markForCheck();
    }

    onDialCodeChange(event) {
        this.selectedPhoneDialCode = event.target.value;
        this.changeDetectorRef.markForCheck();
    }
}
